import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { LogOut } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLang, pick } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { useSite } from "@/lib/site";
import { api, errDetail } from "@/lib/api";
import { Logo, LangToggle } from "@/components/site/Navbar";
import { PlanCard } from "@/components/home/Plans";
import BookingPanel from "@/components/member/BookingPanel";
import RulesGate from "@/components/member/RulesGate";
import { BookingList, WaitlistList, PaymentsList, NotificationsList, StatusBadge } from "@/components/member/Lists";
import { MembershipBox, startCheckout } from "@/components/member/Membership";
import { SessionPanel, EquipmentInfo, TermsGate, TermsCheck } from "@/components/member/Onboarding";
import { KeycardPending, BookingLocked, BookCta, ProfileCard } from "@/components/member/Keycard";
import { HowItWorks } from "@/components/home/Sections";
import { Checkbox } from "@/components/ui/checkbox";

function Stat({ label, children, testId }) {
  return (
    <div className="panel p-5" data-testid={testId}>
      <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">{label}</p>
      <div className="mt-2 text-white">{children}</div>
    </div>
  );
}

function ChoosePlan({ cfg }) {
  const { t, tErr } = useLang();
  const { site } = useSite();
  const [params] = useSearchParams();
  const [okTerms, setOkTerms] = useState(false);
  const [mkt, setMkt] = useState(false);
  const open = site?.capacity?.sales_open !== false;
  const subscribe = async (plan) => {
    try { await startCheckout(plan.id, { accept_terms: okTerms, terms_version: site?.terms_version, marketing_consent: mkt }); } catch (e) { toast.error(tErr(errDetail(e))); }
  };
  const wanted = params.get("plan");
  return (
    <div className="mb-10" data-testid="choose-plan-section">
      <h2 className="mb-2 font-display text-3xl uppercase text-white">{t("member.choosePlan")}</h2>
      <div className="panel mb-5 space-y-3 p-4" data-testid="checkout-terms-box">
        <p className="text-sm text-slate-300" data-testid="billing-explainer">{t("x.billing")}</p>
        <TermsCheck ok={okTerms} setOk={setOkTerms} id="checkout-terms-checkbox" />
        <label className="flex cursor-pointer items-start gap-3 text-sm text-slate-400"><Checkbox checked={mkt} onCheckedChange={(v) => setMkt(!!v)} data-testid="marketing-consent-checkbox" />{t("x.marketing")}</label>
      </div>
      {!cfg?.stripe_connected && <p className="mb-5 rounded-xl border border-amber-500/40 bg-amber-950/30 p-4 text-sm text-amber-200" data-testid="payments-not-connected">{t("pay.notConnected")}</p>}
      {!open && <p className="mb-5 rounded-xl border border-amber-500/40 bg-amber-950/30 p-4 text-sm text-amber-200">{t("plans.paused")}</p>}
      <div className="grid gap-4 md:grid-cols-3">
        {(site?.plans || []).map((p, i) => {
          const ready = p.price_cad != null && p.monthly_hours != null && cfg?.stripe_connected && open && okTerms;
          return <div key={p.id} className={wanted === p.id ? "rounded-2xl ring-2 ring-red-500" : ""}><PlanCard plan={p} idx={i} action={
            <button disabled={!ready} onClick={() => subscribe(p)} data-testid={`subscribe-plan-btn-${i}`} className="btn-red h-11 w-full rounded-full font-semibold disabled:cursor-not-allowed disabled:opacity-40">{t("plans.cta")}</button>} /></div>;
        })}
      </div>
    </div>
  );
}

export default function Member() {
  const { t, lang } = useLang();
  const { logout } = useAuth();
  const [params] = useSearchParams();
  const [dash, setDash] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [tab, setTab] = useState(params.get("tab") || "");
  const load = useCallback(async () => { const { data } = await api.get("/member/dashboard"); setDash(data); return data; }, []);
  useEffect(() => { load(); api.get("/payments/config").then((r) => setCfg(r.data)); }, [load]);
  const state = dash?.booking_state;
  useEffect(() => {
    if (!state || !dash || ["ok", "insufficient_hours"].includes(state)) return undefined;
    const i = setInterval(async () => {
      try {
        const { data } = await api.get("/member/status");
        if (data.booking_state !== state || data.membership_status !== dash.user.membership_status) {
          const d = await load();
          if (d.booking_state === "ok") { toast.success(lang === "fr" ? "Réservation en ligne activée !" : "Online booking enabled!"); setTab("book"); }
        }
      } catch { /* keep polling */ }
    }, 8000);
    return () => clearInterval(i);
  }, [state, dash, load, lang]);

  if (!dash) return <div className="grid min-h-screen place-items-center bg-[#08080a] text-slate-500">{t("common.loading")}</div>;
  const u = dash.user;
  const active = u.membership_status === "active";
  const goBook = () => { setTab("book"); setTimeout(() => document.getElementById("member-tabs")?.scrollIntoView({ behavior: "smooth" }), 50); };
  const current = tab || (state === "ok" ? "book" : "upcoming");

  return (
    <div className="min-h-screen bg-[#08080a]" data-testid="member-dashboard">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-black/85 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-10">
          <Link to="/" data-testid="member-home-link"><Logo /></Link>
          <div className="flex items-center gap-3">
            <LangToggle />
            <button onClick={logout} data-testid="member-logout-btn" className="flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-sm text-slate-200 hover:border-red-500 hover:text-white"><LogOut size={16} />{lang === "fr" ? "Se déconnecter" : "Log Out"}</button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-10">
        <p className="eyebrow">{t("nav.account")}</p>
        <h1 className="mb-8 font-display text-5xl uppercase text-white" data-testid="member-greeting">{t("member.hello")}, {u.name}</h1>
        <div className="mb-10 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Stat label={t("member.status")} testId="member-status"><StatusBadge s={u.membership_status} /></Stat>
          <Stat label={t("member.plan")} testId="member-plan"><span className="text-lg">{dash.plan ? pick(dash.plan, "name", lang) : t("member.noPlan")}</span></Stat>
          <Stat label={t("member.remaining")} testId="member-remaining-hours"><span className="font-display text-4xl" data-testid="member-hours-value">{Number(u.hours_balance || 0).toFixed(1)}</span> <span className="text-slate-500">h</span></Stat>
          <Stat label={t("member.upcomingCount")} testId="member-upcoming-count"><span className="font-display text-4xl">{dash.upcoming.length}</span></Stat>
        </div>
        <MembershipBox dash={dash} cfg={cfg} onChange={load} />
        {active && !dash.terms_accepted && <TermsGate version={dash.terms_version} onDone={load} />}
        {active && state === "keycard_pending" && <KeycardPending contact={dash.contact} request={dash.keycard_request} onChange={load} />}
        {active && state === "ok" && dash.terms_accepted && <BookCta onClick={goBook} />}
        {active && <SessionPanel onChange={load} />}
        {state === "payment_pending" && <BookingLocked state="payment_pending" />}
        {!active && <><p className="mb-6 mt-6 text-slate-400" data-testid="member-inactive-msg">{t("member.inactive")}</p><ChoosePlan cfg={cfg} /></>}

        <Tabs value={current} onValueChange={setTab} id="member-tabs">
          <TabsList className="mb-6 flex h-auto w-full flex-wrap justify-start gap-1 bg-white/5 p-1">
            {["book", "upcoming", "history", "payments", "alerts", "account"].map((k) => (
              <TabsTrigger key={k} value={k} data-testid={`member-tab-${k}`} className="data-[state=active]:bg-red-600 data-[state=active]:text-white">
                {k === "book" ? (lang === "fr" ? "Réserver une séance au studio" : "Book a Studio Session") : k === "account" ? (lang === "fr" ? "Mon compte" : "My Account") : t(`member.tabs.${k}`)}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="book">
            {state !== "ok" ? <BookingLocked state={state} contact={dash.contact} />
              : !dash.terms_accepted ? <p className="panel p-6 text-amber-200" data-testid="booking-terms-block">{lang === "fr" ? "Acceptez les conditions mises à jour (ci-dessus) pour réserver." : "Accept the updated terms (above) to book."}</p>
              : !dash.rules_accepted ? <RulesGate rules={dash.rules} onAccepted={load} />
              : <BookingPanel dash={dash} onBooked={load} />}
          </TabsContent>
          <TabsContent value="upcoming">
            <BookingList items={dash.upcoming} empty={t("member.upcomingEmpty")} deadlineH={dash.booking_rules.cancellation_deadline_hours} onChange={load} cancellable testId="upcoming-list" />
            <WaitlistList items={dash.waitlist} onChange={load} />
          </TabsContent>
          <TabsContent value="history"><BookingList items={dash.history} empty={t("member.historyEmpty")} testId="history-list" /></TabsContent>
          <TabsContent value="payments"><PaymentsList items={dash.payments} /></TabsContent>
          <TabsContent value="alerts"><NotificationsList items={dash.notifications} /></TabsContent>
          <TabsContent value="account"><ProfileCard user={u} onSaved={load} /></TabsContent>
        </Tabs>
        <div className="mt-12"><HowItWorks embedded /><EquipmentInfo eq={dash.equipment} /></div>
      </main>
    </div>
  );
}
